import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { taiPoFireTragedyEPaperCollectionQuery } from "scmp-app/queries/__generated__/taiPoFireTragedyEPaperCollectionQuery.graphql";

import {
  BottomLink,
  Description,
  FooterContainer,
  FooterLogoLink,
  FooterTnc,
  FooterWrapper,
  HeaderLogoLink,
  ListImage,
  ListItem,
  ListLink,
  ListsContainer,
  ListTitle,
  PageContainer,
  StyledFooterLogo,
  StyledHeaderLogo,
  Title,
  TitleContainer,
  Wrapper,
} from "./styles";
import type { TaiPoFirePaperAppConfig } from "./types";

const query = graphql`
  query taiPoFireTragedyEPaperCollectionQuery {
    appConfig(filter: { entityId: "tai_po_fire_epaper" }) {
      json
    }
  }
`;

const TaiPoPage: RelayComponent<PageProps, taiPoFireTragedyEPaperCollectionQuery> = ({
  preloadedQuery,
}) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const onLinkClick = (pdfLink: string) => {
    sendGA4Tracking({
      action: "click",
      category: "epaper",
      customized_parameters: {
        edition: pdfLink.split("/").pop() ?? "",
      },
      subcategory: "download",
    });
    window.open(pdfLink, "_blank");
  };

  const taiPoData = queryReference?.appConfig?.json as TaiPoFirePaperAppConfig;
  const seo = {
    description: taiPoData.seo.description,
    openGraph: {
      description: taiPoData.seo.openGraph.description,
      images: taiPoData.seo.openGraph.images,
      title: taiPoData.seo.openGraph.title,
    },
    title: taiPoData.seo.title,
  };

  return (
    <>
      <PageSeo {...seo} />
      <PageContainer>
        <Wrapper>
          <HeaderLogoLink href="https://www.scmp.com/">
            <StyledHeaderLogo />
          </HeaderLogoLink>
          <TitleContainer>
            <Title>{taiPoData.title}</Title>
            <Description>{taiPoData.description}</Description>
          </TitleContainer>
          <ListsContainer>
            {taiPoData.lists.map((item, index) => (
              <ListItem
                $disabled={!item.pdfLink}
                key={index}
                onClick={() => item.pdfLink && onLinkClick(item.pdfLink)}
              >
                {item.image && <ListImage alt={item.title} src={item.image} />}
                <ListTitle>{item.title}</ListTitle>
                <ListLink $disabled={!item.pdfLink}>Read the e-paper</ListLink>
              </ListItem>
            ))}
          </ListsContainer>
          <BottomLink href={taiPoData.readMore.link}>{taiPoData.readMore.text}</BottomLink>
        </Wrapper>
        <FooterContainer>
          <FooterWrapper>
            <FooterLogoLink href="https://www.scmp.com/">
              <StyledFooterLogo />
            </FooterLogoLink>
            <FooterTnc>
              © {new Date().getFullYear()} South China Morning Post all rights reserved.
            </FooterTnc>
          </FooterWrapper>
        </FooterContainer>
      </PageContainer>
    </>
  );
};

TaiPoPage.displayName = "TaiPoPage";

const getPageProps = (_context: NextPageContext) => {
  const pageProps = {
    appBarConfiguration: {
      hasDesktopAd: false,
      hasMobileAd: false,
      variant: "none",
    },
  };
  return pageProps;
};

export default withRelay(TaiPoPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
});
